// @ts-strict-ignore
import type { SarWorkTypes } from '@usealloy/typegen';
import type { UserContextValue } from '~/dashboard/context';

export interface MultiCheckboxOther {
  checkboxes: { [checkbox: string]: boolean };
  other: string;
}

export interface SarFilingInstitution {
  is_filing_institution: boolean;
  type_of_financial_institution: Array<string>;
  primary_federal_regulator: string | null;
  filer_name: string | null;
  tin: string | null;
  tin_type: string | null;
  type_of_securities_institution: MultiCheckboxOther;
  institution_id: string | null;
  institution_id_type: string | null;
  address: SarEntityAddress | null;
  alternate_name: string | null;
  internal_control_number: string | null;
  contact_agency: string | null;
  phone_number: Array<string>;
  contact_date: string | null;
  contact_name: string | null;
  filing_institution_contact_office: string | null;
  filing_institution_contact_number: Array<string>;
}

export interface APISarFilingInstitution extends Omit<SarFilingInstitution, 'type_of_securities_institution'> {
  type_of_securities_institution: string[];
}

export interface SarActivityInstitution {
  is_filing_institution: boolean;
  type_of_financial_institution: Array<string>;
  primary_federal_regulator: string | null;
  type_of_gaming_institution: MultiCheckboxOther;
  type_of_securities_institution: MultiCheckboxOther;
  institution_id: string | null;
  institution_id_type: string | null;
  financial_institution_role: Array<string>;
  legal_name: string | null;
  alternate_name: string | null;
  tin: string | null;
  tin_type: string | null;
  address: SarEntityAddress | null;
  internal_control_number: string | null;
  loss_financial_institution: Number | null;
  branch_activity: boolean | null;
  no_branch_activity: boolean | null;
  branch_information: Array<BranchInformation> | null;
}

export interface APISarActivityInstitution extends Omit<SarActivityInstitution, 'type_of_gaming_institution' | 'type_of_securities_institution'> {
  type_of_gaming_institution: string[];
  type_of_securities_institution: string[];
}

export interface BranchInformation {
  branch_role: { [key: string]: boolean };
  address: string | null;
  city: string | null;
  rssd_number: string | null;
  state: string | null;
  zip: string | null;
  country: string | null;
}

export interface SarEntity {
  action_date: string | null;
  alternate_names_encrypted: Array<string> | null;
  dob_encrypted: string | null;
  email_addresses_encrypted: Array<string> | null;
  entity_addresses_encrypted: Array<SarEntityAddress> | null; // these are an array of objects
  entity_token: string | null;
  financial_tins: Array<FinancialTin> | null; // 27
  financial_tins_unknown: boolean;
  first_name_encrypted: string | null;
  gender_encrypted: string | null;
  has_statement: boolean | null;
  id_type: string | null;
  info_unavailable: boolean | null;
  is_entity: boolean | null;
  last_name_encrypted: string | null;
  middle_name_encrypted: string | null;
  naics_code: string | null;
  occupation: string | null;
  phone_numbers_encrypted: Array<string> | Array<SarEntityPhoneNumber> | null;
  relationship_tins: Array<SarInstitutionRelationships> | null; // 24. Relationship of the subject
  role_in_activity: string | null;
  sar_entity_ids: Array<SarIdDoc> | null; // 18.Form of identification of subject
  sar_entity_ids_unknown: boolean;
  statement_in_activity: boolean | null;
  suffix_encrypted: string | null;
  tax_id_encrypted: string | null;
  tin?: string | null;
  tin_type: string | null;
  websites: string[] | null;
  is_deleted?: boolean;
}

export interface DomainTokenDraft {
  domainToken: DomainToken;
  isDraft: boolean;
  submitForm: Function;
  meta: { agentEmail?: string; isAgent?: boolean; isEmployee?: boolean };
  userContext: Partial<UserContextValue>;
}

export interface ReportType {
  isInitial?: boolean;
  isAmended: boolean;
  isContinuing: boolean;
}

export interface DomainTokenReportType extends ReportType {
  domainToken: DomainToken;
}

export interface Pii {
  alternate_names: Array<string> | null;
  middle_name: string | null;
  last_name: string | null;
  phone_numbers: Array<string> | null;
  email_addresses: Array<string> | null;
  tax_id: string | null;
  id_number: string | null;
  id_state: string;
  dob: string;
  suffix: string | null;
  gender: string | null;
  entity_addresses: Array<string> | null;
  idDoc?: SarIdDoc[] | null;
  institutionRelationships?: SarInstitutionRelationships[] | null;
}

export interface RootState {
  sars: SarToken;
}

export interface SarInstitutionRelationships {
  institution_tin: string | null;
  position: { [key: string]: boolean };
  other_value: string | null;
  status: string | null;
  action_date: string | null;
}

export interface SarEntityPhoneNumber {
  value?: string | null;
  ext?: string | null;
  type?: string | null;
}

export interface SarIdDoc {
  country: string | null;
  id_num: string | null;
  id_type: string | null;
  state: string | null;
  unknown?: boolean | null;
  other_value: string | null;
}

export interface SarEntityAddress {
  address: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  zip: string | null;
}

export interface SarIpAddress {
  address: string | null;
  date: Date | null;
  timestamp: string | null;
}

export interface ReportErrors {
  xpath: string;
  description: string;
}

export interface ActivityInformation {
  activity_end_date: string;
  activity_start_date: string;
  amount_involved: string;
  no_amount_involved: boolean;
  commodity_type: string[];
  cumulative_amount: string;
  cusip_number: string[];
  fraud_types: { [fraudSection: string]: { [fraudId: string]: boolean } };
  ip_addresses: IpAddress[];
  market_traded: string[];
  narrative: string;
  product_description: string[];
  cyber_frauds: CyberFraud[];
  selected_transactions: string[];
}

export interface APIActivityInformation {
  activity_end_date: string | null;
  activity_start_date: string | null;
  amount_involved: number | null;
  no_amount_involved: boolean | null;
  commodity_type: string[] | null;
  cumulative_amount: number;
  cusip_number: string[] | null;
  cyber_frauds: CyberFraud[];
  fraud_types: number[];
  ip_addresses: IpAddress[];
  market_traded: string[] | null;
  narrative: string | null;
  product_description: string[] | null;
  selected_transactions: string[];
}

export interface SarReport {
  activity_information: ActivityInformation;
  assigned_agent: SarAssignedAgent | null;
  case_token: string | null;
  entity?: SarEntity;
  entities: SarEntity[] | null;
  investigation_token: string | null;
  is_initial: boolean;
  is_amended: boolean;
  is_continuing: boolean;
  is_draft: boolean;
  is_ninety_day_review: boolean;
  is_sandbox?: boolean | null;
  sar_created_at?: string | null;
  sar_report_token?: string | null;
  sar_report_version?: number | null;
  status_code?: number;
  filing_institution: SarFilingInstitution;
  sar_activity_institution?: SarActivityInstitution;
  report_status?: ReportStatusFinCEN;
  bsa_id?: string | null;
  submission_date?: string | null;
  filename?: string | null;
  errors?: Array<ReportErrors>;
  metadata: SarMetadata;
  latest_report_version?: number | null;
}

export interface APISarReport extends Omit<SarReport, 'filing_institution' | 'activity_information' | 'entities' | 'sar_activity_institution'> {
  entities?: SarEntity[] | null;
  entity?: SarEntity | null;
  filing_institution?: APISarFilingInstitution;
  sar_activity_institution?: APISarActivityInstitution;
  sar_filing_institution?: APISarFilingInstitution;
  activity_information?: APIActivityInformation;
  activity_end_date?: string | null;
  activity_start_date?: string | null;
  amount_involved?: number | null;
  no_amount_involved?: boolean | null;
  commodity_type?: string[] | null;
  cumulative_amount?: number | null;
  cusip_number?: string[] | null;
  fraud_types?: number[];
  ip_addresses?: IpAddress[];
  market_traded?: string[] | null;
  product_description?: string[] | null;
  cyber_frauds?: CyberFraud[];
  selected_transactions?: string[];
  narrative?: string;
}

export interface SarMetadata {
  // {
  //   question_1: [{ ...metadata }],
  //   question_nested_additive: [[{ ...metadata }], [{ ...metadata }]],
  //   page: {
  //     question_1: [{ ...metadata }],
  //     question_nested_additive: [[{ ...metadata }], [{ ...metadata }]],
  //   },
  //   additive_page: [{
  //    question_1: [{ ...metadata }],
  //    question_nested_additive: [[{ ...metadata }], [{ ...metadata }]],
  //   }],
  // }
  [prop: string]:
  Array<SarMetadatum>
  | Array<Array<SarMetadatum>>
  | { [prop: string]: Array<SarMetadatum> | Array<Array<SarMetadatum>> }
  | Array<{ [prop: string]: Array<SarMetadatum> | Array<Array<SarMetadatum>> }>;
}

export interface SarMetadatum {
  is_unknown: boolean;
  errors: ValidationErrors;
  key: string;
}

export interface ValidationErrors {
  [error_key: string]: ValidationError | null;
}

export interface ValidationError {
  level: string;
  message: string;
}

export interface IpAddress {
  address: string;
  date: string;
  timestamp: string;
}

export interface CyberFraud {
  sar_cyber_fraud_id?: number | null;
  sar_report_id?: number | null;
  sar_cyber_event_date: string | null;
  sar_cyber_event_type: string | null;
  sar_cyber_event_value: string | null;
  other_value: string | null;
}

export interface FinancialTin {
  accounts: Array<{
    account_number: string | null;
    closed: boolean | null;
  }>;
  institution_tin: string | null;
  non_us_financial_institution: boolean | null;
  unknown?: boolean | null;
}

export interface SarWizard {
  currentStep: SarWizardStep;
}

export interface DomainToken {
  domain: string;
  token: string;
  version?: string;
}

export interface DomainFilters {
  domain: string;
  filters?: string[];
  meta: { agentEmail?: string; isAgent?: boolean; isEmployee?: boolean };
  token?: string;
  version?: number;
  userContext?: Partial<UserContextValue>;
}

export interface SarToken {
  domainToken: DomainToken;
  report: SarReport;
}

export interface SarWizardStep {
  step: 'home' | 'filingInst' | 'entity' | 'fraud' | 'narrative' | 'complete';
  error: boolean;
  complete: boolean;
}

export interface EntityPayload {
  payload: EntityReducer;
}

export interface EntityReducer {
  data: any; // object doesn't work here
  entityToken: string;
  financialTINsCounter: number;
  formIdentificationCounter: number;
  relationshipToInstitutionCounter: number;
  subjectAddressCounter: number;
}

// This is for cases list view:
export type ReportStatus = 'Action Required' | 'Draft' | 'Processing' | 'Submitted' | 'Accepted' | 'No Report' | 'Ready for Review';

// This is for SAR view:
export type ReportStatusFinCEN = 'REJECTED_CORRECTIONS_REQUIRED' | 'ACCEPTED_CORRECTIONS_REQUIRED' | 'PROCESSING' | 'SUBMITTED' | 'ACCEPTED_CORRECTIONS_UNNEEDED' | 'ACCEPTED_CORRECTIONS_OPTIONAL' | 'READY_FOR_REVIEW' | null;

export const reportStatus = {
  acceptedOptional: 'ACCEPTED_CORRECTIONS_OPTIONAL' as ReportStatusFinCEN,
  acceptedRequired: 'ACCEPTED_CORRECTIONS_REQUIRED' as ReportStatusFinCEN,
  acceptedUnneeded: 'ACCEPTED_CORRECTIONS_UNNEEDED' as ReportStatusFinCEN,
  processing: 'PROCESSING' as ReportStatusFinCEN,
  readyForReview: 'READY_FOR_REVIEW' as ReportStatusFinCEN,
  rejectedRequired: 'REJECTED_CORRECTIONS_REQUIRED' as ReportStatusFinCEN,
  submitted: 'SUBMITTED' as ReportStatusFinCEN,
};

export const lockedStatuses = [reportStatus.submitted, reportStatus.processing];

export type SarWorkType = SarWorkTypes;

export const sarWorkType = {
  requestReview: 'sar_review_assigned',
  flag90DayReview: 'sar_ninety_day_review_added',
  unflag90DayReview: 'sar_ninety_day_review_removed',
} as const;

export interface ReportWork {
  assignedAgentId?: number;
  assignedAgentToken?: string;
  note?: string;
  notificationDays?: number;
  workType: SarWorkType;
}

export interface Form {
  steps: { [tabName: string]: Controls };
}
export interface Controls {
  canAddPages?: boolean;
  controls: Array<FormConfiguration>;
  header?: string;
  initialPageValue?: () => object;
  pageLabel?: string;
  multiPageLabel?: string;
  path?: string[];
  subheader?: string[];
}
interface Validation {
  dependency?: Array<string>;
  validator: (value: any, dependencyValue?: any) => { [errorKey: string]: boolean }; // validator function should return an object of errorKeys, which will be used to access the proper message and key the error in metadata
}

export interface DisableCondition {
  dependency?: string[];
  isFieldDisabled: (any) => boolean;
}

export interface FormConfiguration {
  question?: number | string;
  label?: string;
  optional?: string;
  prop: string;
  type: string;
  width?: string;
  style?: object;
  customizations?: object; // @todo: revisit this todd once we officially do this
  placeholder?: string;
  options?: Array<any>;
  minLength?: number | string;
  maxLength?: number | string;
  multiTypes?: Array<FormConfiguration>;
  required?: boolean;
  group?: Array<FormConfiguration>;
  button?: string;
  position?: number;
  section?: string;
  trash?: boolean;
  headingSmall?: boolean;
  validation?: Validation;
  path?: Array<string>;
  initialValue?: any;
  canBeUnknown?: true | 'REQUIRED';
  unknownSectionController?: Array<string>;
  disabled?: DisableCondition;
  tooltip?: string;
}

export interface MultiFormsTypes {
  form: FormConfiguration;
  hasMultipleSections?: boolean;
  isAdditiveSection?: boolean;
  parentSectionIndex?: number;
  renderFormType: (
    form: FormConfiguration,
    sectionIndex?: number,
    isAdditiveSection?: boolean,
    hasMultipleSections?: boolean,
    parentSectionIndex?: number,
  ) => any;
  sectionIndex?: number;
}

export type SarAssignedAgent = {
  first_name: string;
  last_name: string;
  agent_token: string;
  email: string;
};
